import {
  onUpdated,
  ref,
  toRefs,
  watch,
  nextTick,
  useContext,
  computed,
  onMounted
} from '@nuxtjs/composition-api'
import { getCurrentInstance, Ref } from '@vue/composition-api'
import { debounce } from '~/utils/function'
import { DEBOUNCE_DEFAULT } from '~/constants/debounce'
import { computeFieldState } from '~/utils/form'
import { ClientValidator, FieldChange } from '~/models/form/types'
import { CLASSIFIED_FORM_NS } from '~/store/modules/shared/classifieds/form/state'
import {
  ADD_CLIENT_VALIDATOR,
  SET_FIELD_VALUE
} from '~/store/modules/shared/classifieds/form/mutation-types'

export function useClassifiedForm(
  props?: any,
  {
    onValue,
    onError,
    onSpecs,
    onSuggestions,
    clientValidator
  }: {
    onValue?: ((v: Ref<any>) => void) | null
    onError?: ((v: Ref<any>) => void) | null
    onSpecs?: ((v: Ref<any>) => void) | null
    onSuggestions?: ((v: Ref<any>) => void) | null
    clientValidator?: Function | null
  } = {
    onValue: null,
    onError: null,
    onSpecs: null,
    onSuggestions: null,
    clientValidator: null
  }
) {
  const { emit } = getCurrentInstance() as any

  const { store } = useContext()

  const value = ref<any | null>(null)
  const error = ref<string | null>(null)
  const suggestions = ref<any | null>(null)

  if (props) {
    const { fieldError, fieldValue, fieldSuggestions, specs } = toRefs(props)

    watch(fieldError, e => (onError ? onError(ref(e)) : (error.value = e)), {
      immediate: true
    })

    watch(fieldValue, v => (onValue ? onValue(ref(v)) : (value.value = v)), {
      immediate: true
    })

    watch(
      fieldSuggestions,
      v => (onSuggestions ? onSuggestions(ref(v)) : (suggestions.value = v)),
      {
        immediate: true
      }
    )

    if (onSpecs) {
      watch(specs, v => onSpecs(ref(v)), {
        immediate: true
      })
    }
  }

  const formIsDispatched = computed(
    () => store.state.classifieds.form.isDispatched
  )

  const inEditMode = computed(() => store.state.classifieds.form.editMode)

  function addClientValidator(validator: ClientValidator) {
    return store.commit(
      `${CLASSIFIED_FORM_NS}/${ADD_CLIENT_VALIDATOR}`,
      validator
    )
  }

  onMounted(() => {
    if (clientValidator) {
      addClientValidator({
        key: props.name,
        validator: () => {
          return clientValidator(error) // also passing the error ref here in case we need it
        }
      })
    }
  })

  async function valueChanged(name: string, value: any, options?: any) {
    if (formIsDispatched.value) {
      store.commit(`${CLASSIFIED_FORM_NS}/${SET_FIELD_VALUE}`, {
        name,
        value
      })

      emit('change', { name, value })
      return
    }

    await store.dispatch(`${CLASSIFIED_FORM_NS}/fieldChanged`, {
      field: {
        name,
        value,
        options
      }
    })

    store.dispatch(`${CLASSIFIED_FORM_NS}/sendIncompleteClassifiedData`)

    await store.dispatch(`${CLASSIFIED_FORM_NS}/loadSchema`)
  }

  async function valuesChanged(fields: FieldChange[]) {
    if (formIsDispatched.value) {
      for (const field of fields) {
        const { name, value } = field
        store.commit(`${CLASSIFIED_FORM_NS}/${SET_FIELD_VALUE}`, {
          name,
          value
        })
      }

      return
    }

    await store.dispatch(`${CLASSIFIED_FORM_NS}/fieldsChanged`, fields)
    await store.dispatch(`${CLASSIFIED_FORM_NS}/loadSchema`)
  }

  const debouncedSchemaLoad = debounce(() => {
    store.dispatch(`${CLASSIFIED_FORM_NS}/loadSchema`)
  }, DEBOUNCE_DEFAULT)

  const debouncedIncompleteClassifiedSend = debounce(() => {
    store.dispatch(`${CLASSIFIED_FORM_NS}/sendIncompleteClassifiedData`)
  }, DEBOUNCE_DEFAULT)

  async function debouncedValueChanged(
    name: string,
    value: any,
    options?: any
  ) {
    if (formIsDispatched.value) {
      store.commit(`${CLASSIFIED_FORM_NS}/${SET_FIELD_VALUE}`, {
        name,
        value
      })
      emit('change', { name, value })

      return
    }

    await store.dispatch(`${CLASSIFIED_FORM_NS}/fieldChanged`, {
      field: {
        name,
        value,
        options
      }
    })

    debouncedSchemaLoad()
    debouncedIncompleteClassifiedSend()
  }

  const isValid = computed(() => computeFieldState(error.value))

  onUpdated(async () => {
    // TODO: consider using the following only in dispatched fieldsets if there is a performance impact
    await nextTick()
    emit('update')
  })

  const createDatalistElementId = (name: Ref<string>) =>
    `form-handler-datalist-${name.value}`

  return {
    isValid,
    value,
    error,
    suggestions,
    debouncedValueChanged,
    inEditMode,
    formIsDispatched,
    addClientValidator,
    createDatalistElementId,
    valuesChanged,
    valueChanged
  }
}
